import React, { useState, FC, ChangeEvent, FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getRedirectPath } from "../../utils/authUtils";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import BackendStatus from "../../components/ui/BackendStatus";
import brandLogo from "../../assets/dhoondh2-logo.svg";
import { PasswordFormData, PasswordFormErrors } from "../../types/components";
import authService from "../../services/auth.service";
import { hashPassword } from "../../utils/passwordhash";
import Toast from "../../components/ui/Toast";
import { LocalStorageUser } from "../../types/localStorage";

interface PasswordSetupProps {
  state: {
    userId: string;
    mobileNumber: string;
    requestType : "SignUp" | "ResetPassword"
  };
  // No props defined in the original component, but keeping the interface for consistency
}
const PasswordSetup: FC = () => {
  const [formData, setFormData] = useState<PasswordFormData>({
    password: "",
    confirmPassword: "",
    otp: "",
  });

  const [errors, setErrors] = useState<PasswordFormErrors>({});
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { setupPassword } = useAuth();
  const navigate = useNavigate();
  const location: PasswordSetupProps = useLocation();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof PasswordFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: PasswordFormErrors = {};

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.otp) {
      newErrors.otp = "OTP is required";
    } else if (formData.otp.length < 6) {
      newErrors.otp = "OTP must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleResendOTP = async () => {
    // In a real app, this would resend the OTP
    try {
      await authService.resendOtp({...location.state, type: 'Mobile'}); 
      setErrors({});
    } catch (error : any) {
      console.log("Error >>", error)
      setErrors({otp: "Failed to send OTP"});
    }
    // For demo purposes, just show a message
  };
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      console.log("Setting up password for:", location.state);
      const result = await setupPassword({
        userId: location.state.userId,
        mobileOtp: formData.otp,
        password: hashPassword(formData.password, location.state.mobileNumber), // hashing the password before passing to backend
      });
      console.log("Password setup successful:", result);
      // TODO show success snackbar here
      setSuccess(true);
      await delay(1000);
      const role = result?.user?.role;
      // Show success toast and navigate after a short delay
      const performNavigate = (path: string) => {
        setToast({ message: "Signed in successfully", type: "success" });
        setTimeout(() => navigate(path, { replace: true }), 1000);
      };

      // // Check if there's a return URL from the application flow
      // const from = location.state?.from;
      // if (from) {
      //   performNavigate(from);
      //   return;
      // }

      // Get the appropriate redirect path based on user role and status
      const redirectPath = getRedirectPath(
        role as LocalStorageUser["role"] | null
      );
      performNavigate(redirectPath);
      
      // navigate("/login");
    } catch (error: any) {
      console.error("Password setup failed:", error);
      setErrors({ submit: error.message || "Password setup failed" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <div className="max-w-md w-full space-y-8">
        <BackendStatus />

        <div>
          <div className="mx-auto flex items-center justify-center">
            <img src={brandLogo} alt="Dhoondh" className="h-10 aspect-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-black">
            Setup Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create a secure password for your account
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              placeholder="Create a password"
              showPasswordToggle
            />

            <Input
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
              placeholder="Confirm your password"
              showPasswordToggle
            />
          </div>
          <div className="space-y-4">
            <Input
              label="Enter OTP"
              name="otp"
              type="number"
              value={formData.otp}
              onChange={handleChange}
              error={errors.otp}
              required
              placeholder="123456"
              maxLength={6}
              className="tracking-widest"
            />

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?{" "}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className="font-medium text-primary-lightGreen hover:text-primary-black transition-colors duration-300"
                >
                  Resend OTP
                </button>
              </p>
            </div>
          </div>
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Setup Failed
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {errors.submit}
                  </div>
                </div>
              </div>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Password set successfully
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              {loading ? "Setting up..." : "Complete Setup"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordSetup;
